'use client';

import { type FC, useTransition } from 'react';
import { useRouter } from '@bprogress/next/app';

import { Select } from '@/components/ui/select/select';
import type { TOption } from '@/components/ui/select/types';
import { URLBuilder } from '@/utils/URLBuilder';
import { APIBARA_FILTER_PARAMS } from '@/utils/auctions/apibaraFilters';
import {
  DEFAULT_LOT_SORT,
  FILTER_PARAMS,
  isLotSort,
  lotSortOptions,
  type TListingMode,
  type TLotSort,
} from '@/utils/auctions/lotFilters';

/**
 * The listing's order, above the cards (spec 017; owner feedback 2026-09-10:
 * not a form field — it belongs with the list it orders, in the site's own
 * dropdown). Changing it navigates at once, keeps every filter, drops the page
 * number (a new order is a new first page) and writes nothing for the default.
 */

type TAuctionSortControl = {
  sort: TLotSort;
  /** Sold mode reverses what the date sorts mean, so it reverses their wording too. */
  mode: TListingMode;
  /** The raw address parameters, so a change keeps every filter it does not name. */
  searchParams: Record<string, string | string[] | undefined>;
  makeSlug: string | null;
  modelSlug: string | null;
};


export const AuctionSortControl: FC<TAuctionSortControl> = ({ sort, mode, searchParams, makeSlug, modelSlug }) => {
  const options: TOption[] = lotSortOptions(mode).map(option => ({ value: option.value, label: option.label }));

  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const apply = (option: TOption | null) => {
    const next = option && isLotSort(String(option.value)) ? (String(option.value) as TLotSort) : DEFAULT_LOT_SORT;
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(searchParams)) {
      if (
        value === undefined ||
        key === FILTER_PARAMS.sort ||
        key === FILTER_PARAMS.page ||
        key === APIBARA_FILTER_PARAMS.cursor ||
        key === FILTER_PARAMS.make ||
        key === FILTER_PARAMS.model
      ) {
        continue;
      }

      if (Array.isArray(value)) value.forEach(entry => params.append(key, entry));
      else params.set(key, value);
    }

    if (next !== DEFAULT_LOT_SORT) params.set(FILTER_PARAMS.sort, next);

    startTransition(() => {
      router.push(URLBuilder.paths.getAuctionLots(1, params, makeSlug, modelSlug));
    });
  };

  return (
    <div className={isPending ? 'opacity-70 transition-opacity' : 'transition-opacity'}>
      <Select
        id="auction-sort"
        label="Sortowanie"
        options={options}
        value={options.find(option => option.value === sort) ?? options[0]}
        onChange={apply}
        placeholder={options[0].label}
        menuPlacement="auto"
        className="w-full sm:w-72"
      />
    </div>
  );
};
